import {
  PositionActionKey,
  PositionActionRequest,
  PositionActionResponse,
} from '../models/position-action.model'
import { Observable } from 'rxjs'

/**
 * Dispatch surface for every matching action the positions workbench can take (PVAL-26403).
 *
 * A service (rather than the grid calling HttpClient directly) because the actions endpoint is a
 * TASK endpoint: it enqueues work and the graph is reconciled later, so the call site should not be
 * coupled to the transport. Mirrors how the rest of positions-2 binds an abstract service in the
 * panel's providers and swaps the implementation there.
 */
export abstract class PositionsTaskService {
  /**
   * Submit one action. The caller applies its local {@link MatchingService} mutation only after
   * this completes, so a rejected action never leaves the grids showing a state the backend refused.
   */
  abstract submit(
    request: PositionActionRequest,
  ): Observable<PositionActionResponse>
}

/**
 * Whether the panel dispatches to the real `POST /flex-positions/actions`.
 *
 * This is the "flag" the ticket asks for, and it is a module constant on purpose: positions-2 has
 * NO feature-flag mechanism (no LaunchDarkly binding, no env-driven toggle) — the panel's other
 * pending-backend seams are all handled by swapping the provider in
 * `positions-2-panel.component.ts`, and this follows that precedent rather than inventing a
 * flag system for one endpoint.
 *
 * NOW `true`. {@link PositionActionRequest} was checked against the real contract when it was
 * flipped: `FlexPositionAction` on helios !487 (PVAL-26414 — match / unmatch / match-additional)
 * and !491 (PVAL-26308 — ignore), which together define the endpoint.
 *
 * ⚠ Both of those merge requests were still OPEN and UNDEPLOYED at that point (!491 targets !487's
 * branch, not `master`), so until they merge and ship, every action gets a transport failure the
 * grid surfaces as a toast and applies nothing locally. That is the intended failure mode — the
 * panel is not reachable by production users — but it is why this constant survives rather than
 * being inlined: set it back to `false` to fall onto {@link StubPositionsTaskService} if the panel
 * has to be demoed before the backend lands.
 */
export const POSITION_ACTIONS_ENDPOINT_AVAILABLE = true

/** Human-readable label per action, for logs and (later) error messaging. */
export const ACTION_LABEL: Record<PositionActionKey, string> = {
  match: 'Match',
  unmatch: 'Unmatch',
  ignore: 'Ignore',
  unignore: 'Unignore',
}
